import { useEffect, useMemo, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { Target, Trophy, Zap, Flame, Rocket, Sparkles, TrendingUp, LineChart as LineIcon } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer, CartesianGrid, Dot } from "recharts";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { fmtBRL, fmtInt, fmtPct, pct } from "@/lib/format";

// Marcos motivacionais em % da meta.
const MILESTONES = [
  { at: 0,   label: "Bora começar!",             icon: Sparkles, color: "text-slate-400" },
  { at: 25,  label: "Primeiro quarto conquistado", icon: Zap,     color: "text-sky-400" },
  { at: 50,  label: "Metade cravada!",           icon: Flame,    color: "text-amber-400" },
  { at: 75,  label: "Reta final — 75%!",         icon: Rocket,   color: "text-orange-400" },
  { at: 100, label: "META BATIDA 🏆",             icon: Trophy,   color: "text-emerald-400" },
];

const activeMilestone = (percent) => {
  let m = MILESTONES[0];
  for (const it of MILESTONES) if (percent >= it.at) m = it;
  return m;
};

const nextMilestone = (percent) => MILESTONES.find((m) => m.at > percent) || null;

// Dispara animação de confete em 3 rajadas de posições diferentes.
const fireConfetti = () => {
  const defaults = { spread: 70, ticks: 200, gravity: 1, decay: 0.94, startVelocity: 45, zIndex: 9999 };
  confetti({ ...defaults, particleCount: 90, origin: { y: 0.6, x: 0.15 } });
  setTimeout(() => confetti({ ...defaults, particleCount: 120, origin: { y: 0.6, x: 0.5 } }), 120);
  setTimeout(() => confetti({ ...defaults, particleCount: 90, origin: { y: 0.6, x: 0.85 } }), 240);
};

// Chave de storage por vendedora+mês para não disparar mais que 1x por meta batida.
const bateuStorageKey = (name, monthLabel) => `flua_meta_bateu_${name}_${monthLabel || "geral"}`;

// Gera lista dos últimos 6 meses (YYYY-MM), do mais antigo ao atual.
const last6Months = () => {
  const now = new Date();
  const out = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
};

const monthShort = (ym) => {
  const [y, m] = ym.split("-");
  return new Date(Number(y), Number(m) - 1, 1)
    .toLocaleDateString("pt-BR", { month: "short" })
    .replace(".", "");
};

export default function MinhaMetaWidget({
  vendedorName = "",
  clientes = [],
  allClientes = [],
  todosClientes = [],
  vendedoresAtivos = [],
  plans = [],
  meta = 0,
  metasHistoricas = {},
  commissionPct = 0,
  bonusPct = 0,
  monthLabel = "",
  rank = null,        // posição da vendedora no ranking do mês
  totalVend = 0,      // total de vendedoras ativas
}) {
  const priceOf = (nome) => Number(plans.find((p) => p.name === nome)?.price || 0);

  const { receita, vendas } = useMemo(() => {
    const meus = clientes.filter((c) => (c.vendedor || "") === vendedorName);
    return {
      receita: meus.reduce((s, c) => s + priceOf(c.plano_escolhido), 0),
      vendas: meus.length,
    };
  }, [clientes, plans, vendedorName]); // eslint-disable-line

  const historico = useMemo(() => {
    const meses = last6Months();
    const map = Object.fromEntries(meses.map((m) => [m, 0]));
    for (const c of allClientes) {
      if ((c.vendedor || "") !== vendedorName) continue;
      const ym = (c.created_at || "").slice(0, 7);
      if (ym in map) map[ym] += priceOf(c.plano_escolhido);
    }
    // Média do time: receita total dos vendedores ativos / N vendedores por mês.
    const teamTotals = Object.fromEntries(meses.map((m) => [m, 0]));
    for (const c of todosClientes) {
      const ym = (c.created_at || "").slice(0, 7);
      if (ym in teamTotals) teamTotals[ym] += priceOf(c.plano_escolhido);
    }
    const nVend = Math.max(1, (vendedoresAtivos && vendedoresAtivos.length) || 1);
    return meses.map((m) => {
      const metaMes = metasHistoricas[m] !== undefined ? Number(metasHistoricas[m]) : meta;
      return {
        mes: monthShort(m),
        ym: m,
        receita: Number(map[m].toFixed(2)),
        media_time: Number((teamTotals[m] / nVend).toFixed(2)),
        meta: metaMes,
      };
    });
  }, [allClientes, todosClientes, plans, vendedorName, meta, metasHistoricas, vendedoresAtivos]); // eslint-disable-line

  const percent = pct(receita, meta || 1);
  const percentClamp = Math.min(100, Math.max(0, percent));
  const bateu = meta > 0 && receita >= meta;
  const faltando = Math.max(0, meta - receita);
  const comissaoBase = receita * commissionPct;
  const bonus = bateu ? comissaoBase * bonusPct : 0;
  const totalMes = comissaoBase + bonus;
  const marco = activeMilestone(percent);
  const proximo = nextMilestone(percent);
  const MarcoIcon = marco.icon;

  const ticketMedio = vendas > 0 ? receita / vendas : (plans[0]?.price || 0);
  const vendasParaMeta = ticketMedio > 0 && faltando > 0 ? Math.ceil(faltando / ticketMedio) : 0;

  // Confete + vibração quando bate a meta pela primeira vez (por mês).
  const firedRef = useRef(false);
  useEffect(() => {
    if (!bateu || !vendedorName) return;
    const key = bateuStorageKey(vendedorName, monthLabel);
    if (firedRef.current) return;
    let already = false;
    try { already = window.localStorage.getItem(key) === "1"; } catch (e) { console.debug("localStorage read (bateu flag) unavailable:", e?.message); }
    if (already) { firedRef.current = true; return; }
    firedRef.current = true;
    fireConfetti();
    if (navigator.vibrate) {
      try { navigator.vibrate([200, 90, 200, 90, 400]); } catch (e) { console.debug("vibrate unavailable:", e?.message); }
    }
    try { window.localStorage.setItem(key, "1"); } catch (e) { console.debug("localStorage write (bateu flag) unavailable:", e?.message); }
  }, [bateu, vendedorName, monthLabel]);

  const glow = bateu
    ? "shadow-[0_0_60px_-15px_rgba(16,185,129,0.55)]"
    : percent >= 75 ? "shadow-[0_0_60px_-15px_rgba(249,115,22,0.45)]"
    : percent >= 50 ? "shadow-[0_0_60px_-20px_rgba(245,158,11,0.35)]"
    : "";
  const barTone = bateu
    ? "bg-emerald-500"
    : percent >= 75 ? "bg-orange-500"
    : percent >= 50 ? "bg-amber-500"
    : percent >= 25 ? "bg-sky-500"
    : "bg-primary";

  return (
    <div
      className={`rounded-2xl border-2 ${bateu ? "border-emerald-500/50" : "border-primary/30"} bg-gradient-to-br from-card via-card to-primary/5 p-6 ${glow} relative overflow-hidden`}
      data-testid="minha-meta-widget"
    >
      <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
      {bateu && (
        <div className="absolute -bottom-20 -left-20 w-56 h-56 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
      )}

      <div className="relative flex items-start justify-between flex-wrap gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
            <Target className="w-3.5 h-3.5" /> Minha meta {monthLabel && `· ${monthLabel}`}
          </div>
          <div className="mt-1 font-heading text-2xl font-extrabold">
            Olá, {vendedorName || "vendedora"} 👋
          </div>
          <div className="text-sm text-muted-foreground mt-0.5">
            {meta > 0 ? (
              bateu
                ? "Você bateu a meta deste mês. Cada venda extra agora é bônus!"
                : `Faltam ${fmtBRL(faltando)} para bater a meta.`
            ) : (
              "Sua gerente ainda não definiu uma meta este mês."
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {rank && totalVend > 0 && (
            <div
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold ${
                rank === 1 ? "text-amber-400 border-amber-400/40 bg-amber-400/10"
                : rank <= 3 ? "text-emerald-500 border-emerald-500/40 bg-emerald-500/10"
                : rank <= Math.ceil(totalVend / 2) ? "text-primary border-primary/40 bg-primary/10"
                : "text-muted-foreground border-border bg-muted"
              }`}
              data-testid="rank-position"
              title="Sua posição no ranking do mês por receita"
            >
              <Trophy className="w-4 h-4" />
              <span>#{rank} de {totalVend}</span>
              {rank === 1 && <span className="text-[10px]">🏆 Líder</span>}
              {rank > 1 && rank <= 3 && <span className="text-[10px]">Top 3</span>}
              {rank > 3 && rank <= Math.ceil(totalVend / 2) && <span className="text-[10px]">Top {Math.ceil(totalVend / 2)}</span>}
            </div>
          )}
          <div className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold ${marco.color} border-current bg-current/10`}>
            <MarcoIcon className="w-4 h-4" />
            <span>{marco.label}</span>
          </div>
        </div>
      </div>

      <div className="relative mb-4">
        <div className="flex items-baseline justify-between mb-2">
          <div>
            <span className="font-heading text-4xl font-extrabold tabular-nums">{fmtBRL(receita)}</span>
            {meta > 0 && (
              <span className="text-sm text-muted-foreground ml-2 tabular-nums">/ {fmtBRL(meta)}</span>
            )}
          </div>
          <div className="text-right">
            <div className={`font-heading text-3xl font-extrabold tabular-nums ${bateu ? "text-emerald-500" : "text-primary"}`}>
              {fmtPct(percentClamp)}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">da meta</div>
          </div>
        </div>
        <div className="relative h-3 rounded-full bg-muted overflow-hidden">
          <div
            className={`h-full ${barTone} transition-all duration-1000 ease-out`}
            style={{ width: `${percentClamp}%` }}
            data-testid="minha-meta-barra"
          />
          {[25, 50, 75].map((pt) => (
            <div key={pt} className="absolute top-0 bottom-0 w-px bg-background/70" style={{ left: `${pt}%` }} />
          ))}
        </div>
        {proximo && !bateu && (
          <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5">
            <TrendingUp className="w-3 h-3" />
            Próximo marco: <span className="font-semibold text-foreground">{proximo.label}</span> em {fmtPct(proximo.at - percentClamp)}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="rounded-lg border border-border bg-background p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Vendas no mês</div>
          <div className="font-heading text-xl font-bold tabular-nums mt-0.5">{fmtInt(vendas)}</div>
          {vendasParaMeta > 0 && <div className="text-[10px] text-muted-foreground mt-0.5">+{vendasParaMeta} para bater</div>}
        </div>
        <div className="rounded-lg border border-border bg-background p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Comissão base</div>
          <div className="font-heading text-xl font-bold tabular-nums mt-0.5 text-primary">{fmtBRL(comissaoBase)}</div>
          <div className="text-[10px] text-muted-foreground mt-0.5">{fmtPct(commissionPct * 100)} sobre receita</div>
        </div>
        <div className={`rounded-lg border p-3 ${bateu ? "border-emerald-500/40 bg-emerald-500/10" : "border-border bg-background"}`}>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Bônus meta</div>
          <div className={`font-heading text-xl font-bold tabular-nums mt-0.5 ${bateu ? "text-emerald-500" : "text-muted-foreground"}`}>
            {bateu ? fmtBRL(bonus) : "—"}
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            {bateu ? `+${fmtPct(bonusPct * 100)} aplicado` : `+${fmtPct(bonusPct * 100)} ao bater`}
          </div>
        </div>
        <div className={`rounded-lg border-2 p-3 ${bateu ? "border-emerald-500/50 bg-emerald-500/10" : "border-primary/30 bg-primary/5"}`}>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total a receber</div>
          <div className={`font-heading text-2xl font-extrabold tabular-nums mt-0.5 ${bateu ? "text-emerald-500" : "text-primary"}`}>
            {fmtBRL(totalMes)}
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">Estimativa deste mês</div>
        </div>
      </div>

      {/* Histórico últimos 6 meses */}
      <div className="rounded-lg border border-border bg-background/60 p-4" data-testid="historico-6m">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <LineIcon className="w-3.5 h-3.5" />
            <span className="uppercase tracking-widest">Últimos 6 meses · realizado × meta</span>
          </div>
          <Badge variant="outline" className="text-[10px]">Meta atual: {fmtBRL(meta)}</Badge>
        </div>
        <div className="w-full h-[140px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={historico} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.35} />
              <XAxis dataKey="mes" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis width={54} tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                     tickFormatter={(v) => fmtBRL(v).replace(/\s/g, "")} />
              {meta > 0 && (
                <ReferenceLine y={meta} stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" label={{ value: "Meta atual", position: "right", fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
              )}
              <Tooltip
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                formatter={(val, name) => [
                  fmtBRL(val),
                  name === "receita" ? "Você" : name === "media_time" ? "Média do time" : "Meta do mês",
                ]}
                labelFormatter={(l) => `Mês: ${l}`}
              />
              <Line
                type="stepAfter"
                dataKey="meta"
                stroke="hsl(var(--muted-foreground))"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                dot={false}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="media_time"
                stroke="#f59e0b"
                strokeWidth={2}
                strokeDasharray="2 3"
                dot={{ r: 3, fill: "#f59e0b", stroke: "#78350f", strokeWidth: 1 }}
                activeDot={{ r: 5 }}
                isAnimationActive={true}
              />
              <Line
                type="monotone"
                dataKey="receita"
                stroke="hsl(var(--primary))"
                strokeWidth={3}
                dot={(props) => {
                  const { cx, cy, payload, index } = props;
                  const metaPt = Number(payload?.meta || 0);
                  const hit = metaPt > 0 && Number(payload?.receita || 0) >= metaPt;
                  return (
                    <Dot
                      key={payload?.ym || index}
                      cx={cx}
                      cy={cy}
                      r={hit ? 6 : 4}
                      fill={hit ? "#10b981" : "hsl(var(--primary))"}
                      stroke={hit ? "#065f46" : "hsl(var(--card))"}
                      strokeWidth={2}
                    />
                  );
                }}
                activeDot={{ r: 7 }}
                isAnimationActive={true}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-4 flex-wrap">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-[3px] bg-primary rounded-full inline-block"></span>Você</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-[3px] bg-amber-500 rounded-full inline-block" style={{ borderTop: "1px dashed #78350f" }}></span>Média do time</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-[2px] bg-muted-foreground rounded-full inline-block opacity-70"></span>Meta vigente</span>
          <span className="ml-auto">Pontos <span className="text-emerald-500 font-semibold">verdes</span> = meta batida no mês</span>
        </div>
      </div>
    </div>
  );
}

